import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { packPer } from '../seed/wms-fg.seed'
import { deleteRow, findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import {
  fail,
  handle,
  idOf,
  nextDocNo,
  pad,
  parse,
  requireRole,
  roleCodesOf,
  statusCodeOf,
  txLog,
  workflowRunner
} from './c07.shared'
import { createPickingDiscrepancy } from './discrepancy.handler'
import { notify, wmsRoute } from './notification.shared'
import { withoutRepack } from './repackGuard'
import { escalateHours } from './slaSetting'

/**
 * Endpoint custom C09 Outbound FG (WF-OFG-01, entity `delivery_plan`) — `/api/v1/wms/outbound-fg/*`:
 * - OFG-01: `stock-availability`, `next-sj`, `import-commit`, `edit-forecast`, `bongkar-plan` (rencana Bongkar Kemasan),
 *   `release` (= PENGAJUAN ke Supervisor: FIFO + penugasan + bongkar; prototype v7.38), `release-pull` (Tarik Pengajuan),
 *   `release-decide` (Supervisor / Kadiv setelah SLA: Setujui → tugas terbit, atau Tolak + alasan), `reassign`,
 *   `ship` (Tandai Shipped → Surat Jalan), `cancel-sj`, `archive`, `revise` (→ tugas Return-to-Stock bila ada surplus staging).
 * - OFG-04: `unpack-scan` + `unpack-complete` (langkah pertama tugas bila ada Bongkar), `pick-gate`, `pick-unit`,
 *   `pick-not-found`, `stage-rack` (Bawa ke ZS-01).
 * - OFG-06: `rts-lift`, `rts-gate`, `rts-place`.
 * Transisi status mengikuti TL 12B WF-OFG-01; Release/Ship/Cancel SJ/Archive dijalankan handler (WORKFLOW-CUSTOM).
 */

const ofgFlow = workflowRunner('WF-OFG-01', 'delivery_plan')
const STAGING_CODE = 'ZS-01'
const STOCKED_UNITS = ['Karton', 'Bal', 'Slop'] as const
type Unit = 'Karton' | 'Bal' | 'Slop' | 'Pack'
const UNITS: Unit[] = ['Karton', 'Bal', 'Slop', 'Pack']

const up = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()
const planOf = (id: unknown) => {
  const row = findRow('delivery_plan', String(id ?? ''))
  if (!row) fail('Order tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return row
}
const linesOf = (plan: LocalRow) => readTable('delivery_plan_line').filter(line => idOf(line.plan_id) === plan.id)
const tasksOf = (plan: LocalRow) =>
  readTable('fg_picking_task')
    .filter(task => idOf(task.plan_id) === plan.id)
    .sort((a, b) => Number(a.seq ?? 0) - Number(b.seq ?? 0))
const itemsOf = (task: LocalRow) => readTable('fg_picking_task_item').filter(item => idOf(item.task_id) === task.id)
const planItems = (plan: LocalRow) => tasksOf(plan).flatMap(task => itemsOf(task).map(item => ({ task, item })))
const labelOf = (id: unknown) => findRow('fg_label', idOf(id)) as LocalRow
const skuOf = (id: unknown) => findRow('mt_product', idOf(id)) as LocalRow
const palletOf = (id: unknown) => findRow('pallet', idOf(id))
const palletByCode = (code: string) =>
  readTable('pallet').find(row => row.pallet_kind === 'FG' && up(row.code) === up(code))
const userName = (id: unknown) => {
  const user = findRow('directus_users', idOf(id))
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ')
}
const unitOfLine = (line: LocalRow) => String(line.unit_type ?? 'Karton') as Unit
const packOf = (skuCode: string, unit: string) => {
  const per = packPer(skuCode)
  return unit === 'Karton' ? per.karton : unit === 'Bal' ? per.bal : unit === 'Slop' ? per.slop : 1
}

/* ------------------------------------------------------------ periode & Surat Jalan */

/** Periode `YYYY-MM-Wn` — minggu dihitung Senin–Minggu dalam bulan (18 Agu 2026 = W4). */
export function currentPeriod(date = new Date()) {
  const first = new Date(date.getFullYear(), date.getMonth(), 1)
  const offset = (first.getDay() + 6) % 7
  return `${date.getFullYear()}-${pad(date.getMonth() + 1, 2)}-W${Math.ceil((date.getDate() + offset) / 7)}`
}

/** Nomor SJ berikutnya — seri tahunan dengan pool nomor bebas (nomor yang dibatalkan dipakai lagi). */
export function nextSjNo(date = new Date()) {
  const year = String(date.getFullYear())
  const used = new Set(
    readTable('delivery_plan')
      .map(plan => /^(\d{3})\/SJ\/\d{2}\/(\d{4})$/.exec(String(plan.sj_no ?? '')))
      .filter(match => match?.[2] === year)
      .map(match => Number(match?.[1]))
  )
  let n = 1
  while (used.has(n)) n += 1
  return `${pad(n, 3)}/SJ/${pad(date.getMonth() + 1, 2)}/${year}`
}

/* ------------------------------------------------------------ stok & FIFO */

const prodDateOf = (label: LocalRow) =>
  String(findRow('fg_labeler_batch', idOf(label.batch_id))?.prod_date ?? '9999-12-31')

/** Label tersedia (IN_STOCK, di rak) untuk SKU+kemasan, urut FIFO tanggal produksi lalu nomor label. */
export function fifoLabels(skuId: string, unit: string) {
  if (!(STOCKED_UNITS as readonly string[]).includes(unit)) return []
  /* Repack (v7.50 A-03) hanya keluar lewat Permintaan Gabungan — tidak pernah ke order Pack tunggal. */
  return withoutRepack(readTable('fg_label'))
    .filter(
      label =>
        idOf(label.sku_id) === skuId &&
        label.level === unit &&
        label.label_status === 'IN_STOCK' &&
        (label.pallet_id || label.container_id)
    )
    .sort((a, b) => prodDateOf(a).localeCompare(prodDateOf(b)) || String(a.label_no).localeCompare(String(b.label_no)))
}

export function stockAvailability(body: { items?: { sku_id: string; unit_type: string }[] }) {
  return (body.items ?? []).map(item => ({
    sku_id: item.sku_id,
    unit_type: item.unit_type,
    available: fifoLabels(item.sku_id, item.unit_type).length
  }))
}

const drawPallet = (palletId: unknown) => {
  const pallet = palletOf(palletId)
  if (!pallet) return
  const left = Math.max(0, Number(pallet.occupied ?? 0) - 1)
  updateRow('pallet', pallet.id as string, {
    occupied: left,
    ...(left === 0 ? { sku_id: null, unit_type: null, pallet_status: 'EMPTY' } : {}),
    ...auditFieldsForUpdate()
  })
}
const refillPallet = (palletId: unknown, skuId: unknown, unit: string) => {
  const pallet = palletOf(palletId)
  if (!pallet) return
  updateRow('pallet', pallet.id as string, {
    occupied: Number(pallet.occupied ?? 0) + 1,
    sku_id: skuId,
    unit_type: unit,
    pallet_status: 'IN_USE',
    ...auditFieldsForUpdate()
  })
}

/* ------------------------------------------------------------ OFG-01: draft & forecast */

type LineInput = { sku_id?: string; unit_type?: string; qty?: number }

export function cleanLines(lines: LineInput[] | undefined) {
  const result: { sku_id: string; unit_type: Unit; qty: number }[] = []
  for (const line of lines ?? []) {
    const unit = (line.unit_type ?? 'Karton') as Unit
    const qty = Number(line.qty ?? 0)
    if (!line.sku_id || qty <= 0) continue
    if (!UNITS.includes(unit)) fail(`Kemasan ${unit} tidak dikenal.`)
    const existing = result.find(item => item.sku_id === line.sku_id && item.unit_type === unit)
    if (existing) existing.qty += qty
    else result.push({ sku_id: line.sku_id, unit_type: unit, qty })
  }
  return result
}

export function createPlan(customerId: string, period: string, lines: ReturnType<typeof cleanLines>, note: string) {
  const planNo = nextDocNo('PLAN', 'delivery_plan', 'plan_no')
  const id = newId()
  const { statusId, submissionId } = ofgFlow.start(id)
  const plan = insertRow('delivery_plan', {
    ...auditFieldsForCreate(),
    id,
    plan_no: planNo,
    customer_id: customerId,
    period,
    status_id: statusId,
    submission_id: submissionId,
    sj_no: null,
    shipped_at: null,
    is_void: false,
    void_reason: null,
    voided_at: null
  })
  lines.forEach(line => insertLine(plan, line))
  txLog('FG Outbound', 'OFG-01', 'plan-import', planNo, note)
  return plan
}

function insertLine(plan: LocalRow, line: { sku_id: string; unit_type: Unit; qty: number }) {
  return insertRow('delivery_plan_line', {
    ...auditFieldsForCreate(),
    id: newId(),
    plan_id: plan.id,
    sku_id: line.sku_id,
    unit_id: null,
    unit_type: line.unit_type,
    qty_plan: line.qty,
    qty_allocated: 0
  })
}

export type ImportRow = {
  customer_id?: string
  sku_id?: string
  unit_type?: string
  qty?: number
  period?: string
  mode?: 'replace' | 'skip' | 'new'
}

export function importCommit(body: { rows?: ImportRow[] }) {
  requireRole('Import planning oleh Admin WH FG.', 'admin_wh_fg')
  const rows = (body.rows ?? []).filter(row => row.customer_id && row.sku_id && Number(row.qty) > 0)
  if (!rows.length) fail('Tidak ada baris valid untuk diimpor.')
  const groups = new Map<string, ImportRow[]>()
  rows.forEach(row => {
    const key = `${row.customer_id}|${row.period ?? currentPeriod()}`
    groups.set(key, [...(groups.get(key) ?? []), row])
  })
  const created: string[] = []
  const replaced: string[] = []
  let skipped = 0
  groups.forEach((groupRows, key) => {
    const [customerId, period] = key.split('|')
    const forecast = readTable('delivery_plan').find(
      plan => idOf(plan.customer_id) === customerId && plan.period === period && statusCodeOf(plan) === 'OFG_FORECAST'
    )
    const fresh: ReturnType<typeof cleanLines> = []
    groupRows.forEach(row => {
      const unit = (row.unit_type ?? 'Karton') as Unit
      const dup = forecast
        ? linesOf(forecast).find(line => idOf(line.sku_id) === row.sku_id && unitOfLine(line) === unit)
        : undefined
      if (dup) {
        if (row.mode === 'replace') {
          updateRow('delivery_plan_line', dup.id as string, { qty_plan: Number(row.qty), ...auditFieldsForUpdate() })
          replaced.push(String(forecast?.plan_no))
        } else skipped += 1
        return
      }
      fresh.push({ sku_id: String(row.sku_id), unit_type: unit, qty: Number(row.qty) })
    })
    const merged = cleanLines(fresh)
    if (!merged.length) return
    if (forecast) {
      merged.forEach(line => insertLine(forecast, line))
      replaced.push(String(forecast.plan_no))
    } else
      created.push(
        String(
          createPlan(customerId, period, merged, `Import planning periode ${period} — ${merged.length} baris`).plan_no
        )
      )
  })
  return { created, updated: [...new Set(replaced)], skipped }
}

export function editForecast(body: { plan_id?: string; customer_id?: string; lines?: LineInput[] }) {
  requireRole('Order Forecast diubah oleh Admin WH FG.', 'admin_wh_fg')
  const plan = planOf(body.plan_id)
  if (statusCodeOf(plan) !== 'OFG_FORECAST') fail('Hanya order Forecast yang bisa diedit.', 'INVALID_FROM_STATUS', 409)
  const lines = cleanLines(body.lines)
  if (!lines.length) fail('Order harus punya minimal satu baris.')
  linesOf(plan).forEach(line => deleteRow('delivery_plan_line', line.id as string))
  lines.forEach(line => insertLine(plan, line))
  updateRow('delivery_plan', plan.id as string, {
    ...(body.customer_id ? { customer_id: body.customer_id } : {}),
    ...auditFieldsForUpdate()
  })
  txLog('FG Outbound', 'OFG-01', 'plan-edit', String(plan.plan_no), `Edit Forecast — ${lines.length} baris`)
  return { plan_no: plan.plan_no }
}

export function archivePlan(body: { plan_id?: string }) {
  requireRole('Arsip order oleh Admin WH FG.', 'admin_wh_fg')
  const plan = planOf(body.plan_id)
  if (statusCodeOf(plan) !== 'OFG_FORECAST')
    fail('Hanya order Forecast yang bisa diarsipkan.', 'INVALID_FROM_STATUS', 409)
  ofgFlow.run(plan, 'ACT_OFG_EXPIRE', ['ACT_OFG_ADMIN_FG'])
  txLog('FG Outbound', 'OFG-01', 'plan-archive', String(plan.plan_no), `Forecast periode ${plan.period} diarsipkan`)
  return { plan_no: plan.plan_no }
}

/* ------------------------------------------------------------ OFG-01: release & penugasan */

type Assignment = { operator_id?: string; sku_id?: string; unit_type?: string; qty?: number }

function requireOperators(assignments: Assignment[]) {
  assignments.forEach(item => {
    const roles = roleCodesOf(String(item.operator_id ?? ''))
    if (!roles.includes('operator_wh_fg')) fail('Petugas harus Operator WH FG.', 'ERR_VALIDATION_FAILED')
  })
}

/** Bagi label FIFO ke petugas sesuai `assignments` dan tulis tugas + item. */
function writeTasks(plan: LocalRow, assignments: Assignment[], labelsByKey: Map<string, LocalRow[]>) {
  const taskByOperator = new Map<string, LocalRow>()
  tasksOf(plan).forEach(task => taskByOperator.set(idOf(task.operator_id), task))
  let seq = tasksOf(plan).length
  const taken = new Map<string, number>()
  assignments.forEach(item => {
    const key = `${item.sku_id}|${item.unit_type}`
    const pool = labelsByKey.get(key) ?? []
    const from = taken.get(key) ?? 0
    const qty = Number(item.qty ?? 0)
    const chunk = pool.slice(from, from + qty)
    taken.set(key, from + chunk.length)
    if (!chunk.length) return
    const operatorId = String(item.operator_id)
    let task = taskByOperator.get(operatorId)
    if (!task) {
      seq += 1
      task = insertRow('fg_picking_task', {
        ...auditFieldsForCreate(),
        id: newId(),
        plan_id: plan.id,
        operator_id: operatorId,
        task_status: 'ASSIGNED',
        seq
      })
      taskByOperator.set(operatorId, task)
    }
    chunk.forEach(label => {
      insertRow('fg_picking_task_item', {
        ...auditFieldsForCreate(),
        id: newId(),
        task_id: task?.id,
        label_id: label.id,
        from_pallet_id: label.pallet_id ?? null,
        container_id: label.container_id ?? null,
        discrepancy_id: null,
        substitute_of_item_id: null,
        item_status: 'PENDING'
      })
      updateRow('fg_label', label.id as string, { label_status: 'ALLOCATED', ...auditFieldsForUpdate() })
    })
  })
  /** Picking FG ditugaskan → tiap operator yang kebagian label mendapat satu notifikasi. */
  const labelsPerOperator = new Map<string, number>()
  readTable('fg_picking_task_item').forEach(entry => {
    const owner = [...taskByOperator.entries()].find(([, task]) => task.id === idOf(entry.task_id))?.[0]
    if (owner) labelsPerOperator.set(owner, (labelsPerOperator.get(owner) ?? 0) + 1)
  })
  const touched = new Set(assignments.map(item => String(item.operator_id)))
  touched.forEach(operatorId =>
    notify({
      to: { userIds: [operatorId] },
      type: 'PICKING_ASSIGNED',
      title: `Tugas picking FG order ${plan.plan_no}`,
      body: `Order ${plan.plan_no} dirilis — ${labelsPerOperator.get(operatorId) ?? 0} label perlu di-pick. Buka Picking FG — Operator.`,
      link: wmsRoute('outbound-fg/picking-fg-operator'),
      ref: { type: 'fg_plan', id: String(plan.id) }
    })
  )
}

/* ------------------------------------------------------------ alokasi FIFO & rencana Bongkar Kemasan */

type Allocation = {
  labelsByKey: Map<string, LocalRow[]>
  allocated: Map<string, number>
  shortage: Map<string, number>
  taken: Set<string>
}

/** Alokasi FIFO per baris order (label In Stock saat ini). Tidak menulis apa pun. */
function allocatePlan(plan: LocalRow): Allocation {
  const labelsByKey = new Map<string, LocalRow[]>()
  const allocated = new Map<string, number>()
  const shortage = new Map<string, number>()
  const taken = new Set<string>()
  linesOf(plan).forEach(line => {
    const key = `${idOf(line.sku_id)}|${unitOfLine(line)}`
    const pool = fifoLabels(idOf(line.sku_id), unitOfLine(line)).filter(label => !taken.has(String(label.id)))
    const want = Number(line.qty_plan ?? 0)
    const picked = [...(labelsByKey.get(key) ?? []), ...pool.slice(0, want)]
    picked.forEach(label => taken.add(String(label.id)))
    labelsByKey.set(key, picked)
    allocated.set(String(line.id), Math.min(want, pool.length))
    shortage.set(String(line.id), Math.max(0, want - pool.length))
  })
  return { labelsByKey, allocated, shortage, taken }
}

/** Kemasan induk yang boleh dibuka untuk menutup kekurangan satu kemasan (terkecil dulu). */
const PARENT_UNITS: Partial<Record<Unit, Unit[]>> = { Bal: ['Karton'], Slop: ['Bal', 'Karton'] }
const unitLetter = (unit: string) => (unit === 'Karton' ? 'C' : unit === 'Bal' ? 'B' : 'S')

export type BongkarSourcePlan = {
  label_id: string
  label_no: string
  unit: Unit
  rack: string
  yield_qty: number
  send_qty: number
  left_qty: number
}
export type BongkarPlan = {
  sku_id: string
  sku_code: string
  unit_type: Unit
  short: number
  ok: boolean
  send: number
  left: number
  sources: BongkarSourcePlan[]
}

/**
 * Rencana bongkar untuk `need` unit `unit` SKU `skuId`: induk FIFO dibuka (Bal dulu, lalu Karton untuk Slop). Satu induk
 * menghasilkan `yield` unit target; hasil lebih dari kebutuhan jadi sisa yang disimpan kembali (laporan 22).
 */
export function planBongkar(skuId: string, unit: Unit, need: number, taken: Set<string>): BongkarPlan {
  const sku = skuOf(skuId)
  const code = String(sku?.code ?? '')
  const sources: BongkarSourcePlan[] = []
  let remaining = need
  for (const parentUnit of PARENT_UNITS[unit] ?? []) {
    const yieldQty = Math.round(packOf(code, parentUnit) / packOf(code, unit))
    if (yieldQty <= 0) continue
    for (const parent of fifoLabels(skuId, parentUnit)) {
      if (remaining <= 0) break
      if (taken.has(String(parent.id))) continue
      const send = Math.min(yieldQty, remaining)
      sources.push({
        label_id: String(parent.id),
        label_no: String(parent.label_no),
        unit: parentUnit,
        rack: String(palletOf(parent.pallet_id)?.code ?? findRow('container', idOf(parent.container_id))?.code ?? ''),
        yield_qty: yieldQty,
        send_qty: send,
        left_qty: yieldQty - send
      })
      taken.add(String(parent.id))
      remaining -= send
    }
    if (remaining <= 0) break
  }
  return {
    sku_id: skuId,
    sku_code: code,
    unit_type: unit,
    short: need,
    ok: remaining <= 0 && sources.length > 0,
    send: need - Math.max(0, remaining),
    left: sources.reduce((sum, item) => sum + item.left_qty, 0),
    sources
  }
}

/** Rencana bongkar tiap baris yang kurang stok dan bisa ditutup dengan membuka induk (hanya Bal / Slop). */
export function bongkarPlans(plan: LocalRow, allocation = allocatePlan(plan)) {
  const taken = new Set(allocation.taken)
  return linesOf(plan)
    .filter(line => (allocation.shortage.get(String(line.id)) ?? 0) > 0 && PARENT_UNITS[unitOfLine(line)])
    .map(line =>
      planBongkar(idOf(line.sku_id), unitOfLine(line), allocation.shortage.get(String(line.id)) as number, taken)
    )
}

export function bongkarPlan(body: { plan_id?: string }) {
  const plan = planOf(body.plan_id)
  if (statusCodeOf(plan) !== 'OFG_FORECAST') return []
  return bongkarPlans(plan)
}

const BPO_OPEN = ['PENDING_SPV', 'ASSIGNED']
const bpoOf = (plan: LocalRow) => readTable('fg_unpack_order').filter(row => idOf(row.plan_id) === plan.id)
const sourcesOf = (bpo: LocalRow) => readTable('fg_unpack_source').filter(row => idOf(row.unpack_id) === bpo.id)

/** Lepas induk yang ditahan pengajuan bongkar (ditolak / ditarik). */
function releaseBpoHold(bpo: LocalRow, status: string) {
  sourcesOf(bpo).forEach(source => {
    const label = findRow('fg_label', idOf(source.label_id))
    if (label?.label_status === 'ALLOCATED')
      updateRow('fg_label', label.id as string, { label_status: 'IN_STOCK', ...auditFieldsForUpdate() })
  })
  updateRow('fg_unpack_order', bpo.id as string, { bpo_status: status, ...auditFieldsForUpdate() })
}

/* ------------------------------------------------------------ OFG-01: release = pengajuan ke Supervisor */

type BongkarAssignment = { sku_id?: string; unit_type?: string; operator_id?: string }

/** Admin mengajukan Release: alokasi FIFO dihitung + dibagi ke petugas; stok belum dikunci (prototype v7.38). */
export function release(body: { plan_id?: string; assignments?: Assignment[]; bongkar?: BongkarAssignment[] }) {
  requireRole('Pengajuan Release oleh Admin WH FG.', 'admin_wh_fg')
  const plan = planOf(body.plan_id)
  if (statusCodeOf(plan) !== 'OFG_FORECAST')
    fail('Hanya order Forecast yang bisa di-Release.', 'INVALID_FROM_STATUS', 409)
  const lines = linesOf(plan)
  const allocation = allocatePlan(plan)
  const total = [...allocation.allocated.values()].reduce((sum, n) => sum + n, 0)
  const plans = bongkarPlans(plan, allocation).filter(item => item.ok)
  if (total <= 0 && !plans.length) fail('Tidak ada stok yang bisa dialokasikan untuk order ini.', 'ERR_NO_STOCK')
  const assignments = (body.assignments ?? []).filter(item => Number(item.qty) > 0)
  requireOperators(assignments)
  lines.forEach(line => {
    const key = `${idOf(line.sku_id)}|${unitOfLine(line)}`
    const need = allocation.allocated.get(String(line.id)) ?? 0
    const given = assignments
      .filter(item => `${item.sku_id}|${item.unit_type}` === key)
      .reduce((sum, item) => sum + Number(item.qty), 0)
    if (given !== need) {
      const sku = skuOf(line.sku_id)
      fail(`Alokasi ${sku?.code} ${unitOfLine(line)} belum pas — dialokasikan ${need}, ditugaskan ${given}.`)
    }
  })
  const bongkarOps = body.bongkar ?? []
  plans.forEach(item => {
    const op = bongkarOps.find(row => row.sku_id === item.sku_id && row.unit_type === item.unit_type)?.operator_id
    if (!op) fail(`Pilih petugas untuk Bongkar ${item.sku_code} ${item.unit_type}.`)
    requireOperators([{ operator_id: op }])
  })
  const now = new Date().toISOString()
  const created = plans.map(item => {
    const bpo = insertRow('fg_unpack_order', {
      ...auditFieldsForCreate(),
      id: newId(),
      bpo_no: nextDocNo('BPO', 'fg_unpack_order', 'bpo_no'),
      plan_id: plan.id,
      sku_id: item.sku_id,
      target_unit: item.unit_type,
      qty_need: item.send,
      qty_left: item.left,
      bpo_status: 'PENDING_SPV',
      operator_id: bongkarOps.find(row => row.sku_id === item.sku_id && row.unit_type === item.unit_type)?.operator_id,
      task_id: null,
      left_note: null,
      completed_at: null
    })
    item.sources.forEach(source => {
      insertRow('fg_unpack_source', {
        ...auditFieldsForCreate(),
        id: newId(),
        unpack_id: bpo.id,
        label_id: source.label_id,
        source_unit: source.unit,
        yield_qty: source.yield_qty,
        send_qty: source.send_qty,
        left_qty: source.left_qty,
        rack_code: source.rack,
        scanned_at: null
      })
      updateRow('fg_label', source.label_id, { label_status: 'ALLOCATED', ...auditFieldsForUpdate() })
    })
    return bpo
  })
  updateRow('delivery_plan', plan.id as string, {
    release_requested_at: now,
    release_requested_by_id: currentUserId(),
    release_assignments: JSON.stringify(assignments),
    release_rejected_reason: null,
    release_decided_at: null,
    release_decided_by_id: null,
    ...auditFieldsForUpdate()
  })
  ofgFlow.run(plan, 'ACT_OFG_SUBMIT_RELEASE', ['ACT_OFG_ADMIN_FG'])
  txLog(
    'FG Outbound',
    'OFG-01',
    'release-ajukan',
    String(plan.plan_no),
    `${total} unit stok${created.length ? `, bongkar ${created.map(row => row.bpo_no).join(', ')}` : ''} — diajukan ke Supervisor WH FG`
  )
  return { plan_no: plan.plan_no, allocated: total, bongkar: created.map(row => String(row.bpo_no)) }
}

/** Admin menarik pengajuan: order kembali Forecast, induk bongkar dilepas. */
export function releasePull(body: { plan_id?: string }) {
  requireRole('Pengajuan Release ditarik oleh Admin WH FG.', 'admin_wh_fg')
  const plan = planOf(body.plan_id)
  if (statusCodeOf(plan) !== 'OFG_PENDING_SPV')
    fail('Hanya order Menunggu Supervisor yang bisa ditarik.', 'INVALID_FROM_STATUS', 409)
  bpoOf(plan)
    .filter(row => row.bpo_status === 'PENDING_SPV')
    .forEach(row => releaseBpoHold(row, 'CANCELLED'))
  ofgFlow.run(plan, 'ACT_OFG_PULL_RELEASE', ['ACT_OFG_ADMIN_FG'])
  txLog('FG Outbound', 'OFG-01', 'release-tarik', String(plan.plan_no), 'Pengajuan Release ditarik Admin')
  return { plan_no: plan.plan_no }
}

/** Jam sejak pengajuan Release; dipakai disposisi Kadiv. */
export const releaseAgeHours = (plan: LocalRow, now = Date.now()) =>
  plan.release_requested_at ? (now - new Date(String(plan.release_requested_at)).getTime()) / 3_600_000 : 0

/** Pastikan ada tugas picking untuk operator (dipakai tugas yang hanya berisi bongkar). */
function ensureTask(plan: LocalRow, operatorId: string) {
  const existing = tasksOf(plan).find(task => idOf(task.operator_id) === operatorId)
  if (existing) return existing
  return insertRow('fg_picking_task', {
    ...auditFieldsForCreate(),
    id: newId(),
    plan_id: plan.id,
    operator_id: operatorId,
    task_status: 'ASSIGNED',
    seq: tasksOf(plan).length + 1
  })
}

/**
 * Supervisor WH FG memutuskan pengajuan Release; Kadiv hanya setelah SLA `OFG_RELEASE_ESCALATE` (jam dinding, tidak
 * pernah otomatis). Setuju → alokasi dihitung ulang, label dibagi (sisa ikut petugas terakhir baris itu), tugas terbit.
 */
export function releaseDecide(body: { plan_id?: string; approve?: boolean; reason?: string }) {
  const roles = roleCodesOf(currentUserId())
  const isSpv = roles.includes('supervisor_wh_fg')
  const isKadiv = roles.includes('kadiv_wh_fg')
  if (!isSpv && !isKadiv) fail('Keputusan Release oleh Supervisor WH FG.', 'ERR_UNAUTHORIZED_ROLE', 403)
  const plan = planOf(body.plan_id)
  if (statusCodeOf(plan) !== 'OFG_PENDING_SPV')
    fail('Hanya order Menunggu Supervisor yang bisa diputuskan.', 'INVALID_FROM_STATUS', 409)
  /* v7.50 C-13: disposisi dimatikan di Master SLA → tidak pernah naik ke Kadiv */
  const threshold = escalateHours('OFG_RELEASE_ESCALATE')
  const escalated = threshold !== null && releaseAgeHours(plan) >= threshold
  const onBehalf = !isSpv && isKadiv
  if (onBehalf && !escalated)
    fail(
      threshold === null
        ? 'Keputusan Release ada di Supervisor WH FG — disposisi ke Kadiv dimatikan di Master SLA.'
        : `Keputusan Release ada di Supervisor WH FG. Kadiv dapat memutuskan setelah ${threshold} jam.`,
      'ERR_UNAUTHORIZED_ROLE',
      403
    )
  const actor = isSpv ? 'ACT_OFG_SPV_FG' : 'ACT_OFG_KADIV_FG'
  const reason = String(body.reason ?? '').trim()
  const now = new Date().toISOString()
  const decided = { release_decided_at: now, release_decided_by_id: currentUserId(), release_by_disposition: onBehalf }
  const open = bpoOf(plan).filter(row => row.bpo_status === 'PENDING_SPV')

  const reject = (why: string) => {
    open.forEach(row => releaseBpoHold(row, 'REJECTED'))
    updateRow('delivery_plan', plan.id as string, {
      ...decided,
      release_rejected_reason: why,
      ...auditFieldsForUpdate()
    })
    ofgFlow.run(plan, 'ACT_OFG_REJECT_RELEASE', [actor], {}, why)
  }
  if (body.approve === false) {
    if (!reason) fail('Alasan penolakan wajib diisi.')
    reject(reason)
    txLog('FG Outbound', 'OFG-01A', 'release-tolak', String(plan.plan_no), `Release ditolak — ${reason}`)
    return { plan_no: plan.plan_no, approved: false }
  }

  const allocation = allocatePlan(plan)
  const total = [...allocation.allocated.values()].reduce((sum, n) => sum + n, 0)
  if (total <= 0 && !open.length) {
    reject('Release gagal — stok berubah, tidak ada yang bisa dialokasikan.')
    txLog('FG Outbound', 'OFG-01A', 'release-gagal', String(plan.plan_no), 'Stok berubah — order kembali Forecast')
    return { plan_no: plan.plan_no, approved: false, failed: true }
  }
  /* Bagi label sesuai pengajuan; kelebihan alokasi (stok bertambah) ikut petugas terakhir baris itu. */
  const requested = JSON.parse(String(plan.release_assignments ?? '[]')) as Assignment[]
  const assignments: Assignment[] = []
  allocation.labelsByKey.forEach((labels, key) => {
    const [skuId, unit] = key.split('|')
    const rows = requested.filter(item => item.sku_id === skuId && item.unit_type === unit)
    let left = labels.length
    rows.forEach(row => {
      const qty = Math.min(Number(row.qty), left)
      if (qty > 0) assignments.push({ ...row, qty })
      left -= qty
    })
    if (left > 0 && rows.length) {
      const lastOp = rows[rows.length - 1].operator_id
      const last = assignments
        .filter(item => item.operator_id === lastOp && item.sku_id === skuId && item.unit_type === unit)
        .pop()
      if (last) last.qty = Number(last.qty) + left
      else assignments.push({ operator_id: lastOp, sku_id: skuId, unit_type: unit, qty: left })
    }
  })
  writeTasks(plan, assignments, allocation.labelsByKey)
  const bpoSend = new Map<string, number>()
  open.forEach(row => {
    const key = `${idOf(row.sku_id)}|${row.target_unit}`
    bpoSend.set(key, (bpoSend.get(key) ?? 0) + Number(row.qty_need))
    const task = ensureTask(plan, idOf(row.operator_id))
    updateRow('fg_unpack_order', row.id as string, {
      bpo_status: 'ASSIGNED',
      task_id: task.id,
      ...auditFieldsForUpdate()
    })
  })
  linesOf(plan).forEach(line =>
    updateRow('delivery_plan_line', line.id as string, {
      qty_allocated:
        (allocation.allocated.get(String(line.id)) ?? 0) +
        (bpoSend.get(`${idOf(line.sku_id)}|${unitOfLine(line)}`) ?? 0),
      ...auditFieldsForUpdate()
    })
  )
  updateRow('delivery_plan', plan.id as string, {
    ...decided,
    release_rejected_reason: null,
    ...auditFieldsForUpdate()
  })
  ofgFlow.run(plan, 'ACT_OFG_APPROVE_RELEASE', [actor])
  txLog(
    'FG Outbound',
    'OFG-01A',
    'release-setuju',
    String(plan.plan_no),
    `Release disetujui${onBehalf ? ' Kadiv (disposisi)' : ' Supervisor'} — ${total} unit FIFO${open.length ? `, bongkar ${open.map(row => row.bpo_no).join(', ')}` : ''}`
  )
  return { plan_no: plan.plan_no, approved: true, allocated: total, bongkar: open.map(row => String(row.bpo_no)) }
}

export function reassign(body: { plan_id?: string; assignments?: Assignment[] }) {
  requireRole('Penugasan petugas diubah oleh Admin WH FG.', 'admin_wh_fg')
  const plan = planOf(body.plan_id)
  const status = statusCodeOf(plan)
  if (status !== 'OFG_RELEASED' && status !== 'OFG_STAGING')
    fail('Penugasan hanya bisa diubah selama order Released / Staging.', 'INVALID_FROM_STATUS', 409)
  const pending = planItems(plan).filter(({ item }) => item.item_status === 'PENDING')
  if (!pending.length) fail('Tidak ada barang yang belum diambil.')
  const assignments = (body.assignments ?? []).filter(item => Number(item.qty) > 0)
  requireOperators(assignments)
  const labelsByKey = new Map<string, LocalRow[]>()
  pending.forEach(({ item }) => {
    const label = labelOf(item.label_id)
    const key = `${idOf(label.sku_id)}|${label.level}`
    labelsByKey.set(key, [...(labelsByKey.get(key) ?? []), label])
  })
  labelsByKey.forEach((labels, key) => {
    const given = assignments
      .filter(item => `${item.sku_id}|${item.unit_type}` === key)
      .reduce((sum, item) => sum + Number(item.qty), 0)
    if (given !== labels.length) fail(`Alokasi belum pas — tersisa ${labels.length}, ditugaskan ${given}.`)
  })
  pending.forEach(({ item }) => deleteRow('fg_picking_task_item', item.id as string))
  writeTasks(plan, assignments, labelsByKey)
  tasksOf(plan)
    .filter(task => itemsOf(task).length === 0)
    .forEach(task => deleteRow('fg_picking_task', task.id as string))
  tasksOf(plan).forEach(refreshTaskStatus)
  txLog('FG Outbound', 'OFG-01', 'plan-reassign', String(plan.plan_no), `Penugasan diubah — ${pending.length} unit`)
  return { plan_no: plan.plan_no }
}

/* ------------------------------------------------------------ OFG-01: kirim & batal SJ */

export function ship(body: { plan_id?: string }) {
  requireRole('Tandai Shipped oleh Admin WH FG.', 'admin_wh_fg')
  const plan = planOf(body.plan_id)
  if (statusCodeOf(plan) !== 'OFG_STAGED')
    fail('Hanya order Staged yang bisa ditandai Shipped.', 'INVALID_FROM_STATUS', 409)
  const staging = readTable('fg_staging').filter(
    row => idOf(row.plan_id) === plan.id && row.staging_status === 'STAGED'
  )
  if (!staging.length) fail('Belum ada barang di staging untuk order ini.')
  if (openRts(plan).length) fail('Masih ada tugas Return-to-Stock yang belum selesai untuk order ini.')
  const sjNo = nextSjNo()
  ofgFlow.run(plan, 'ACT_OFG_DISPATCH', ['ACT_OFG_ADMIN_FG'], { sj_no: sjNo })
  updateRow('delivery_plan', plan.id as string, { sj_no: sjNo, ...auditFieldsForUpdate() })
  staging.forEach(row => {
    updateRow('fg_staging', row.id as string, { staging_status: 'SHIPPED', ...auditFieldsForUpdate() })
    updateRow('fg_label', idOf(row.label_id), { label_status: 'SHIPPED', ...auditFieldsForUpdate() })
  })
  txLog('FG Outbound', 'OFG-01', 'plan-ship', String(plan.plan_no), `Shipped — SJ ${sjNo}, ${staging.length} unit`)
  return { plan_no: plan.plan_no, sj_no: sjNo, units: staging.length }
}

export function cancelSj(body: { plan_id?: string; reason?: string }) {
  requireRole('Pembatalan Surat Jalan oleh Admin WH FG.', 'admin_wh_fg')
  const plan = planOf(body.plan_id)
  if (statusCodeOf(plan) !== 'OFG_SHIPPED' || !plan.sj_no)
    fail('Hanya order Shipped dengan Surat Jalan yang bisa dibatalkan.', 'INVALID_FROM_STATUS', 409)
  const reason = String(body.reason ?? '').trim()
  if (!reason) fail('Alasan pembatalan wajib diisi.')
  const released = String(plan.sj_no)
  ofgFlow.run(plan, 'ACT_OFG_ROLLBACK_SJ', ['ACT_OFG_ADMIN_FG'], {}, reason)
  updateRow('delivery_plan', plan.id as string, {
    sj_no: null,
    shipped_at: null,
    void_reason: reason,
    voided_at: new Date().toISOString(),
    ...auditFieldsForUpdate()
  })
  const staging = readTable('fg_staging').filter(
    row => idOf(row.plan_id) === plan.id && row.staging_status === 'SHIPPED'
  )
  staging.forEach(row => {
    updateRow('fg_staging', row.id as string, { staging_status: 'STAGED', ...auditFieldsForUpdate() })
    updateRow('fg_label', idOf(row.label_id), { label_status: 'STAGED', ...auditFieldsForUpdate() })
  })
  txLog('FG Outbound', 'OFG-01', 'sj-cancel', String(plan.plan_no), `SJ ${released} dibatalkan — ${reason}`)
  return { plan_no: plan.plan_no, released_no: released, units: staging.length }
}

/* ------------------------------------------------------------ OFG-01: revisi order */

const openRts = (plan: LocalRow) =>
  readTable('fg_rts_task').filter(task => idOf(task.plan_id) === plan.id && task.task_status !== 'COMPLETED')

function refreshTaskStatus(task: LocalRow) {
  const items = itemsOf(task).filter(item => item.item_status !== 'NOT_FOUND')
  const done = items.length > 0 && items.every(item => item.item_status === 'STAGED')
  const started = items.some(item => item.item_status === 'STAGED' || item.item_status === 'PICKED')
  updateRow('fg_picking_task', task.id as string, {
    task_status: done ? 'COMPLETED' : started ? 'IN_PROGRESS' : 'ASSIGNED',
    ...auditFieldsForUpdate()
  })
}

/** Semua unit order sudah di ZS-01 → Staging → Staged. */
function completeStagingIfDone(plan: LocalRow, actors: string[]) {
  if (statusCodeOf(plan) !== 'OFG_STAGING') return false
  const items = planItems(plan).map(row => row.item)
  const resolved = items.filter(item => item.item_status !== 'NOT_FOUND')
  if (!resolved.length || !resolved.every(item => item.item_status === 'STAGED')) return false
  if (openRts(plan).length) return false
  if (bpoOf(plan).some(row => BPO_OPEN.includes(String(row.bpo_status)))) return false
  ofgFlow.run(plan, 'ACT_OFG_COMPLETE_STAGING', actors)
  return true
}

export function revise(body: { plan_id?: string; lines?: LineInput[] }) {
  requireRole('Revisi order oleh Admin WH FG.', 'admin_wh_fg')
  const plan = planOf(body.plan_id)
  const status = statusCodeOf(plan)
  if (!['OFG_RELEASED', 'OFG_STAGING', 'OFG_STAGED'].includes(status))
    fail('Revisi hanya untuk order yang sudah Release dan belum Shipped.', 'INVALID_FROM_STATUS', 409)
  const wanted = new Map(
    (body.lines ?? []).map(line => [`${line.sku_id}|${line.unit_type ?? 'Karton'}`, Math.max(0, Number(line.qty ?? 0))])
  )
  let cancelled = 0
  let added = 0
  const rtsItems: { label: LocalRow; operatorId: string; toPallet: unknown }[] = []
  linesOf(plan).forEach(line => {
    const key = `${idOf(line.sku_id)}|${unitOfLine(line)}`
    if (!wanted.has(key)) return
    const target = wanted.get(key) as number
    const current = planItems(plan).filter(({ item }) => {
      const label = labelOf(item.label_id)
      return `${idOf(label.sku_id)}|${label.level}` === key && item.item_status !== 'NOT_FOUND'
    })
    const diff = target - current.length
    if (diff < 0) {
      // Batalkan dulu yang belum diambil, lalu tarik yang sudah di staging / dibawa (FIFO terbalik: terbaru dulu).
      const order = [...current].sort(
        (a, b) =>
          Number(b.item.item_status === 'PENDING') - Number(a.item.item_status === 'PENDING') ||
          String(labelOf(b.item.label_id).label_no).localeCompare(String(labelOf(a.item.label_id).label_no))
      )
      order.slice(0, -diff).forEach(({ task, item }) => {
        const label = labelOf(item.label_id)
        if (item.item_status === 'PENDING') {
          updateRow('fg_label', label.id as string, { label_status: 'IN_STOCK', ...auditFieldsForUpdate() })
        } else {
          updateRow('fg_label', label.id as string, { label_status: 'RTS_PENDING', ...auditFieldsForUpdate() })
          rtsItems.push({ label, operatorId: idOf(task.operator_id), toPallet: item.from_pallet_id })
        }
        deleteRow('fg_picking_task_item', item.id as string)
        cancelled += 1
      })
    } else if (diff > 0) {
      if (status === 'OFG_STAGED')
        fail('Order sudah Staged — tambahan qty tidak bisa dimasukkan; buat order baru untuk tambahannya.')
      const stock = fifoLabels(idOf(line.sku_id), unitOfLine(line)).slice(0, diff)
      const task =
        tasksOf(plan).find(candidate =>
          itemsOf(candidate).some(item => idOf(labelOf(item.label_id).sku_id) === idOf(line.sku_id))
        ) ?? tasksOf(plan)[0]
      stock.forEach(label => {
        if (!task) return
        insertRow('fg_picking_task_item', {
          ...auditFieldsForCreate(),
          id: newId(),
          task_id: task.id,
          label_id: label.id,
          from_pallet_id: label.pallet_id ?? null,
          container_id: label.container_id ?? null,
          discrepancy_id: null,
          substitute_of_item_id: null,
          item_status: 'PENDING'
        })
        updateRow('fg_label', label.id as string, { label_status: 'ALLOCATED', ...auditFieldsForUpdate() })
        added += 1
      })
    }
    const kept = planItems(plan).filter(({ item }) => {
      const label = labelOf(item.label_id)
      return `${idOf(label.sku_id)}|${label.level}` === key && item.item_status !== 'NOT_FOUND'
    }).length
    updateRow('delivery_plan_line', line.id as string, {
      qty_plan: target,
      qty_allocated: kept,
      ...auditFieldsForUpdate()
    })
  })
  // Item staging yang ditarik: tutup baris fg_staging dan terbitkan tugas Return-to-Stock per operator.
  const rtsTasks: string[] = []
  const byOperator = new Map<string, typeof rtsItems>()
  rtsItems.forEach(entry => byOperator.set(entry.operatorId, [...(byOperator.get(entry.operatorId) ?? []), entry]))
  byOperator.forEach((entries, operatorId) => {
    const rtsNo = nextDocNo('RTS', 'fg_rts_task', 'rts_no')
    const task = insertRow('fg_rts_task', {
      ...auditFieldsForCreate(),
      id: newId(),
      rts_no: rtsNo,
      plan_id: plan.id,
      operator_id: operatorId,
      task_status: 'ASSIGNED',
      source_ref: plan.plan_no
    })
    entries.forEach(({ label, toPallet }) => {
      const staging = readTable('fg_staging').find(
        row => idOf(row.plan_id) === plan.id && idOf(row.label_id) === label.id && row.staging_status === 'STAGED'
      )
      if (staging)
        updateRow('fg_staging', staging.id as string, { staging_status: 'RTS_PENDING', ...auditFieldsForUpdate() })
      insertRow('fg_rts_task_item', {
        ...auditFieldsForCreate(),
        id: newId(),
        task_id: task.id,
        label_id: label.id,
        to_pallet_id: toPallet ?? null,
        actual_pallet_id: null,
        override_reason: null,
        item_status: 'PENDING'
      })
    })
    rtsTasks.push(rtsNo)
  })
  tasksOf(plan).forEach(task => {
    if (!itemsOf(task).length) deleteRow('fg_picking_task', task.id as string)
    else refreshTaskStatus(task)
  })
  const refreshed = planOf(plan.id)
  completeStagingIfDone(refreshed, ['ACT_OFG_ADMIN_FG', 'ACT_OFG_OP_FG'])
  txLog(
    'FG Outbound',
    'OFG-01',
    'plan-revise',
    String(plan.plan_no),
    `Revisi — batal ${cancelled}, tambah ${added}, tugas RTS ${rtsTasks.join(', ') || '—'}`
  )
  return { plan_no: plan.plan_no, cancelled, added, rts_tasks: rtsTasks }
}

/* ------------------------------------------------------------ OFG-04: picking operator */

function taskFor(taskId: unknown) {
  requireRole('Picking dikerjakan Operator WH FG.', 'operator_wh_fg')
  const task = findRow('fg_picking_task', String(taskId ?? ''))
  if (!task) fail('Tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (idOf(task.operator_id) !== currentUserId()) fail('Tugas ini milik operator lain.', 'ERR_NOT_TASK_OWNER', 403)
  return task
}

const itemRack = (item: LocalRow) =>
  String(palletOf(item.from_pallet_id)?.code ?? findRow('container', idOf(item.container_id))?.code ?? '')
const matchesLabel = (label: LocalRow, code: string) => {
  const scanned = up(code)
  const no = up(label.label_no)
  return scanned === no || no.endsWith(`:${scanned}`) || (!!label.gtin && scanned === up(label.gtin))
}

export function pickGate(body: { task_id?: string; pallet_code?: string }) {
  const task = taskFor(body.task_id)
  const code = up(body.pallet_code)
  const items = itemsOf(task).filter(item => item.item_status === 'PENDING' || item.item_status === 'PICKED')
  if (!palletByCode(code)) fail(`${code} bukan pallet FG.`, 'ERR_LOCATION_REJECTED')
  if (openUnpack(task).length)
    fail('Selesaikan Bongkar Kemasan dulu sebelum mengambil unit.', 'ERR_WORKFLOW_STATE_MISMATCH')
  const racks = [...new Set(items.map(itemRack))]
  if (!racks.includes(code))
    fail(
      racks.length
        ? `Pallet ${code} di luar tugas ini. Pindai pallet ${racks.join(' / ')}.`
        : `Pallet ${code} di luar tugas ini.`,
      'ERR_LOCATION_REJECTED'
    )
  return {
    pallet_code: code,
    pending: items.filter(item => item.item_status === 'PENDING' && itemRack(item) === code).length
  }
}

export function pickUnit(body: { task_id?: string; pallet_code?: string; code?: string }) {
  const task = taskFor(body.task_id)
  const rack = up(body.pallet_code)
  const pending = itemsOf(task).filter(item => item.item_status === 'PENDING' && itemRack(item) === rack)
  if (!pending.length) fail(`Tidak ada unit tersisa di ${rack}.`, 'ERR_NOT_IN_TASK')
  const item = pending.find(row => matchesLabel(labelOf(row.label_id), String(body.code ?? '')))
  if (!item) {
    const other = itemsOf(task).find(row => matchesLabel(labelOf(row.label_id), String(body.code ?? '')))
    if (other?.item_status === 'PICKED' || other?.item_status === 'STAGED')
      fail('Unit ini sudah diambil.', 'ERR_DUPLICATE')
    fail(`Bukan unit yang ditugaskan di ${rack}. Ambil unit yang tertera pada tugas.`, 'ERR_NOT_IN_TASK')
  }
  const label = labelOf(item.label_id)
  updateRow('fg_picking_task_item', item.id as string, { item_status: 'PICKED', ...auditFieldsForUpdate() })
  updateRow('fg_label', label.id as string, { label_status: 'PICKED', ...auditFieldsForUpdate() })
  drawPallet(item.from_pallet_id)
  const plan = planOf(task.plan_id)
  if (statusCodeOf(plan) === 'OFG_RELEASED') ofgFlow.run(plan, 'ACT_OFG_START_PICK', ['ACT_OFG_OP_FG'])
  refreshTaskStatus(task)
  return { label_no: label.label_no, remaining: pending.length - 1 }
}

export function pickNotFound(body: { task_id?: string; item_id?: string }) {
  const task = taskFor(body.task_id)
  const item = findRow('fg_picking_task_item', String(body.item_id ?? ''))
  if (!item || idOf(item.task_id) !== task.id) fail('Unit bukan bagian dari tugas ini.', 'ERR_NOT_IN_TASK')
  if (item.item_status !== 'PENDING') fail('Unit ini sudah diproses.')
  const label = labelOf(item.label_id)
  updateRow('fg_picking_task_item', item.id as string, { item_status: 'NOT_FOUND', ...auditFieldsForUpdate() })
  updateRow('fg_label', label.id as string, { label_status: 'MISSING', ...auditFieldsForUpdate() })
  /* C13: unit hilang saat picking → selisih Open (OPN-04), tertaut ke baris tugas (BA 00 WF-011). */
  const disc = createPickingDiscrepancy({
    label: findRow('fg_label', String(label.id)) as LocalRow,
    taskNo: `${String(planOf(task.plan_id)?.plan_no ?? 'picking FG')} #${String(task.seq ?? '')}`,
    page: 'OFG-04'
  })
  updateRow('fg_picking_task_item', item.id as string, { discrepancy_id: disc.id })
  drawPallet(item.from_pallet_id)
  const substitute = fifoLabels(idOf(label.sku_id), String(label.level))[0]
  let substituteInfo: { label_no: string; rack: string } | null = null
  if (substitute) {
    insertRow('fg_picking_task_item', {
      ...auditFieldsForCreate(),
      id: newId(),
      task_id: task.id,
      label_id: substitute.id,
      from_pallet_id: substitute.pallet_id ?? null,
      container_id: substitute.container_id ?? null,
      discrepancy_id: null,
      substitute_of_item_id: item.id,
      item_status: 'PENDING'
    })
    updateRow('fg_label', substitute.id as string, { label_status: 'ALLOCATED', ...auditFieldsForUpdate() })
    substituteInfo = {
      label_no: String(substitute.label_no),
      rack: String(palletOf(substitute.pallet_id)?.code ?? '')
    }
  } else {
    const plan = planOf(task.plan_id)
    const line = linesOf(plan).find(
      candidate => idOf(candidate.sku_id) === idOf(label.sku_id) && unitOfLine(candidate) === label.level
    )
    if (line)
      updateRow('delivery_plan_line', line.id as string, {
        qty_allocated: Math.max(0, Number(line.qty_allocated ?? 0) - 1),
        ...auditFieldsForUpdate()
      })
  }
  txLog(
    'Discrepancy',
    'OFG-04',
    'fg-not-found',
    String(label.label_no),
    `Tidak ditemukan di ${itemRack(item)} — ${substituteInfo ? `pengganti ${substituteInfo.label_no} (${substituteInfo.rack})` : 'tanpa pengganti, stok kurang'}`
  )
  refreshTaskStatus(task)
  return { substitute: substituteInfo }
}

export function stageRack(body: { task_id?: string; pallet_code?: string; zone_code?: string }) {
  const task = taskFor(body.task_id)
  if (up(body.zone_code) !== STAGING_CODE)
    fail(`Itu bukan Area Staging. Scan barcode ${STAGING_CODE}.`, 'ERR_LOCATION_REJECTED')
  const rack = up(body.pallet_code)
  const picked = itemsOf(task).filter(item => item.item_status === 'PICKED' && itemRack(item) === rack)
  if (!picked.length) fail('Belum ada unit yang diambil dari pallet ini.')
  if (itemsOf(task).some(item => item.item_status === 'PENDING' && itemRack(item) === rack))
    fail('Masih ada unit di pallet ini yang belum diambil.')
  const plan = planOf(task.plan_id)
  const stagingZone = readTable('warehouse_zone').find(row => row.code === STAGING_CODE)
  picked.forEach(item => {
    const label = labelOf(item.label_id)
    const sku = skuOf(label.sku_id)
    updateRow('fg_picking_task_item', item.id as string, { item_status: 'STAGED', ...auditFieldsForUpdate() })
    updateRow('fg_label', label.id as string, {
      label_status: 'STAGED',
      pallet_id: null,
      container_id: null,
      zone_id: stagingZone?.id ?? null,
      ...auditFieldsForUpdate()
    })
    insertRow('fg_staging', {
      ...auditFieldsForCreate(),
      id: newId(),
      plan_id: plan.id,
      label_id: label.id,
      qty_pack: packOf(String(sku?.code), String(label.level)),
      picked_by_id: currentUserId(),
      staging_status: 'STAGED'
    })
  })
  refreshTaskStatus(task)
  const completed = completeStagingIfDone(planOf(plan.id), ['ACT_OFG_OP_FG'])
  txLog(
    'FG Outbound',
    'OFG-04',
    'fg-stage',
    String(plan.plan_no),
    `${picked.length} unit dari ${rack} → ${STAGING_CODE}`
  )
  return { staged: picked.length, order_staged: completed }
}

/* ------------------------------------------------------------ OFG-04: Bongkar Kemasan (langkah pertama tugas) */

const openUnpack = (task: LocalRow) =>
  readTable('fg_unpack_order').filter(row => idOf(row.task_id) === task.id && row.bpo_status === 'ASSIGNED')

function unpackFor(bpoId: unknown) {
  requireRole('Bongkar Kemasan dikerjakan Operator WH FG.', 'operator_wh_fg')
  const bpo = findRow('fg_unpack_order', String(bpoId ?? ''))
  if (!bpo) fail('Pengajuan bongkar tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (idOf(bpo.operator_id) !== currentUserId())
    fail('Tugas bongkar ini milik operator lain.', 'ERR_NOT_TASK_OWNER', 403)
  if (bpo.bpo_status !== 'ASSIGNED')
    fail('Bongkar belum ditugaskan atau sudah selesai.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  return bpo
}

/** Scan label induk yang akan dibuka (urutan bebas, tiap induk sekali). */
export function unpackScan(body: { bpo_id?: string; code?: string }) {
  const bpo = unpackFor(body.bpo_id)
  const sources = sourcesOf(bpo)
  const hit = sources.find(source => matchesLabel(labelOf(source.label_id), String(body.code ?? '')))
  if (!hit) {
    const next = sources.find(source => !source.scanned_at)
    fail(
      next
        ? `Label bukan induk yang direncanakan. Pindai ${String(labelOf(next.label_id).label_no)}.`
        : 'Label bukan induk yang direncanakan.',
      'ERR_NOT_IN_TASK'
    )
  }
  if (hit.scanned_at) fail('Induk ini sudah dipindai.', 'ERR_DUPLICATE')
  updateRow('fg_unpack_source', hit.id as string, { scanned_at: new Date().toISOString(), ...auditFieldsForUpdate() })
  const scanned = sourcesOf(bpo).filter(source => source.scanned_at).length
  return { label_no: labelOf(hit.label_id).label_no, scanned, total: sources.length }
}

/** Tempat sisa bongkar: Bal → pallet Bal yang masih muat, Slop → container Slop; bila tak ada, belum berumah. */
function storeLeftover(children: LocalRow[], unit: Unit) {
  if (!children.length) return null
  if (unit === 'Slop') {
    const container = readTable('container').find(row => row.container_type === 'Slop' || row.type === 'Slop')
    if (!container) return null
    children.forEach(child =>
      updateRow('fg_label', child.id as string, { container_id: container.id, ...auditFieldsForUpdate() })
    )
    return String(container.code)
  }
  const pallet = readTable('pallet').find(
    row =>
      row.pallet_kind === 'FG' &&
      Number(row.occupied ?? 0) + children.length <= 24 &&
      ((row.unit_type === 'Bal' && Number(row.occupied ?? 0) > 0) || Number(row.occupied ?? 0) === 0)
  )
  if (!pallet) return null
  children.forEach(child => {
    refillPallet(pallet.id, child.sku_id, 'Bal')
    updateRow('fg_label', child.id as string, {
      pallet_id: pallet.id,
      zone_id: idOf(pallet.zone_id),
      ...auditFieldsForUpdate()
    })
  })
  return String(pallet.code)
}

/** Selesai Bongkar: induk habis, anak berlabel terbit (kirim → PICKED di tugas ini, sisa → disimpan). */
export function unpackComplete(body: { bpo_id?: string }) {
  const bpo = unpackFor(body.bpo_id)
  const sources = sourcesOf(bpo)
  if (sources.some(source => !source.scanned_at))
    fail('Pindai semua induk yang direncanakan sebelum menyelesaikan bongkar.', 'ERR_VALIDATION_FAILED')
  const plan = planOf(bpo.plan_id)
  const task = findRow('fg_picking_task', idOf(bpo.task_id)) as LocalRow
  const unit = String(bpo.target_unit) as Unit
  const leftover: LocalRow[] = []
  let seq = 0
  let sent = 0
  sources.forEach(source => {
    const parent = labelOf(source.label_id)
    const make = (status: string) => {
      seq += 1
      return insertRow('fg_label', {
        ...auditFieldsForCreate(),
        id: newId(),
        label_no: `${String(parent.label_no)}-${unitLetter(unit)}${pad(seq, 2)}`,
        sku_id: parent.sku_id,
        batch_id: parent.batch_id,
        level: unit,
        gtin: null,
        parent_label_id: parent.id,
        zone_id: null,
        pallet_id: null,
        container_id: null,
        label_status: status,
        is_priority_lot: parent.is_priority_lot ?? false
      })
    }
    for (let i = 0; i < Number(source.send_qty); i += 1) {
      const child = make('PICKED')
      sent += 1
      insertRow('fg_picking_task_item', {
        ...auditFieldsForCreate(),
        id: newId(),
        task_id: task.id,
        label_id: child.id,
        from_pallet_id: parent.pallet_id ?? null,
        container_id: parent.container_id ?? null,
        discrepancy_id: null,
        substitute_of_item_id: null,
        item_status: 'PICKED'
      })
    }
    for (let i = 0; i < Number(source.left_qty); i += 1) leftover.push(make('IN_STOCK'))
    if (parent.pallet_id) drawPallet(parent.pallet_id)
    updateRow('fg_label', parent.id as string, {
      label_status: 'UNPACKED',
      pallet_id: null,
      container_id: null,
      ...auditFieldsForUpdate()
    })
  })
  const place = storeLeftover(leftover, unit)
  updateRow('fg_unpack_order', bpo.id as string, {
    bpo_status: 'COMPLETED',
    completed_at: new Date().toISOString(),
    left_note: leftover.length
      ? `${leftover.length} ${unit} sisa ${place ? `disimpan di ${place}` : 'belum berumah — butuh putaway'}`
      : 'Tanpa sisa',
    ...auditFieldsForUpdate()
  })
  if (statusCodeOf(plan) === 'OFG_RELEASED') ofgFlow.run(plan, 'ACT_OFG_START_PICK', ['ACT_OFG_OP_FG'])
  refreshTaskStatus(task)
  txLog(
    'FG Outbound',
    'OFG-04',
    'fg-bongkar',
    String(bpo.bpo_no),
    `Bongkar ${sources.length} induk → ${sent} ${unit} untuk ${String(plan.plan_no)}${leftover.length ? `, sisa ${leftover.length} ${unit} (${place ?? 'belum berumah'})` : ''}`
  )
  return {
    bpo_no: bpo.bpo_no,
    sent,
    left: leftover.length,
    left_place: place,
    racks: [...new Set(sources.map(source => String(palletOf(labelOf(source.label_id).pallet_id)?.code ?? '')))]
  }
}

/* ------------------------------------------------------------ OFG-06: return to stock */

function rtsTaskFor(taskId: unknown) {
  requireRole('Return-to-Stock dikerjakan Operator WH FG.', 'operator_wh_fg')
  const task = findRow('fg_rts_task', String(taskId ?? ''))
  if (!task) fail('Tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (idOf(task.operator_id) !== currentUserId()) fail('Tugas ini milik operator lain.', 'ERR_NOT_TASK_OWNER', 403)
  return task
}
const rtsItemsOf = (task: LocalRow) => readTable('fg_rts_task_item').filter(item => idOf(item.task_id) === task.id)

export function rtsLift(body: { task_id?: string; code?: string; zone_code?: string }) {
  const task = rtsTaskFor(body.task_id)
  if (up(body.zone_code) !== STAGING_CODE)
    fail(`Itu bukan Area Staging. Scan barcode ${STAGING_CODE}.`, 'ERR_LOCATION_REJECTED')
  const item = rtsItemsOf(task).find(
    row => row.item_status === 'PENDING' && matchesLabel(labelOf(row.label_id), String(body.code ?? ''))
  )
  if (!item) fail('Bukan unit dari tugas ini.', 'ERR_NOT_IN_TASK')
  updateRow('fg_rts_task_item', item.id as string, { item_status: 'LIFTED', ...auditFieldsForUpdate() })
  updateRow('fg_rts_task', task.id as string, { task_status: 'IN_PROGRESS', ...auditFieldsForUpdate() })
  return { label_no: labelOf(item.label_id).label_no }
}

export type RtsVerdict = {
  verdict: 'ok' | 'needs_reason' | 'reject'
  title: string
  message: string
  suggested?: string
}

/** Kecocokan pallet tujuan: pallet asal bisa sudah diisi SKU lain → sistem menyarankan pallet lain. */
function rtsVerdict(skuId: string, rackCode: string, code: string): RtsVerdict {
  const pallet = palletByCode(code)
  if (!pallet) return { verdict: 'reject', title: 'Pallet Tidak Dikenal', message: `${code} bukan pallet FG.` }
  const occupied = Number(pallet.occupied ?? 0)
  const suggestion = () =>
    readTable('pallet')
      .filter(row => row.pallet_kind === 'FG' && row.pallet_status !== 'FULL' && row.code !== code)
      .sort((a, b) => String(a.code).localeCompare(String(b.code)))
      .find(
        row =>
          Number(row.occupied ?? 0) < 24 &&
          ((idOf(row.sku_id) === skuId && Number(row.occupied ?? 0) > 0) || Number(row.occupied ?? 0) === 0)
      )?.code as string | undefined
  if (occupied > 0 && pallet.sku_id && idOf(pallet.sku_id) !== skuId)
    return {
      verdict: 'reject',
      title: 'Pallet Berisi SKU Lain',
      message: `${code} sudah berisi SKU lain. Pindai ${suggestion() ?? 'pallet lain'}.`,
      suggested: suggestion()
    }
  if (occupied >= 24)
    return { verdict: 'reject', title: 'Pallet Penuh', message: `${code} sudah penuh (24).`, suggested: suggestion() }
  if (code !== rackCode)
    return {
      verdict: 'needs_reason',
      title: 'Bukan Pallet Asal',
      message: `Pallet asal ${rackCode}, Anda memindai ${code}. Lanjutkan dan catat alasan?`
    }
  return { verdict: 'ok', title: 'Pallet Benar', message: '' }
}

export function rtsGate(body: { task_id?: string; rack_code?: string; pallet_code?: string }) {
  const task = rtsTaskFor(body.task_id)
  const rack = up(body.rack_code)
  const lifted = rtsItemsOf(task).filter(
    item => item.item_status === 'LIFTED' && String(palletOf(item.to_pallet_id)?.code ?? '') === rack
  )
  if (!lifted.length) fail('Belum ada unit yang diangkat untuk pallet ini.')
  const skuId = idOf(labelOf(lifted[0].label_id).sku_id)
  return rtsVerdict(skuId, rack, up(body.pallet_code))
}

export function rtsPlace(body: {
  task_id?: string
  rack_code?: string
  pallet_code?: string
  override_reason?: string
}) {
  const task = rtsTaskFor(body.task_id)
  const rack = up(body.rack_code)
  const code = up(body.pallet_code)
  const lifted = rtsItemsOf(task).filter(
    item => item.item_status === 'LIFTED' && String(palletOf(item.to_pallet_id)?.code ?? '') === rack
  )
  if (!lifted.length) fail('Belum ada unit yang diangkat untuk pallet ini.')
  const first = labelOf(lifted[0].label_id)
  const verdict = rtsVerdict(idOf(first.sku_id), rack, code)
  if (verdict.verdict === 'reject') fail(verdict.message, 'ERR_LOCATION_REJECTED')
  const reason = String(body.override_reason ?? '').trim()
  if (verdict.verdict === 'needs_reason' && !reason)
    fail('Alasan wajib dicatat untuk penempatan ini.', 'ERR_NEEDS_REASON')
  const pallet = palletByCode(code) as LocalRow
  lifted.forEach(item => {
    const label = labelOf(item.label_id)
    refillPallet(pallet.id, label.sku_id, String(label.level))
    updateRow('fg_label', label.id as string, {
      label_status: 'IN_STOCK',
      pallet_id: pallet.id,
      zone_id: idOf(pallet.zone_id),
      ...auditFieldsForUpdate()
    })
    updateRow('fg_rts_task_item', item.id as string, {
      item_status: 'RETURNED',
      actual_pallet_id: pallet.id,
      override_reason: reason || null,
      ...auditFieldsForUpdate()
    })
    const staging = readTable('fg_staging').find(
      row => idOf(row.label_id) === label.id && row.staging_status === 'RTS_PENDING'
    )
    if (staging)
      updateRow('fg_staging', staging.id as string, { staging_status: 'RETURNED', ...auditFieldsForUpdate() })
  })
  const left = rtsItemsOf(task).filter(item => item.item_status !== 'RETURNED')
  updateRow('fg_rts_task', task.id as string, {
    task_status: left.length ? 'IN_PROGRESS' : 'COMPLETED',
    ...auditFieldsForUpdate()
  })
  if (!left.length) {
    const plan = planOf(task.plan_id)
    completeStagingIfDone(plan, ['ACT_OFG_ADMIN_FG', 'ACT_OFG_OP_FG'])
  }
  txLog(
    'FG Outbound',
    'OFG-06',
    'fg-rts',
    String(task.rts_no),
    `${lifted.length} unit → ${code}${reason ? ` · alasan: ${reason}` : ''}`
  )
  return { placed: lifted.length, pallet_code: code, task_done: !left.length }
}

/* ------------------------------------------------------------ registrasi */

export function registerOutboundFgHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/outbound-fg'
  const post = <T>(path: string, run: (body: T) => unknown) =>
    mock.onPost(`${base}/${path}`).reply(config => handle(() => run(parse<T>(config.data))))
  post('stock-availability', stockAvailability)
  post('next-sj', () => ({ sj_no: nextSjNo(), period: currentPeriod() }))
  post('import-commit', importCommit)
  post('edit-forecast', editForecast)
  post('archive', archivePlan)
  post('bongkar-plan', bongkarPlan)
  post('release', release)
  post('release-pull', releasePull)
  post('release-decide', releaseDecide)
  post('reassign', reassign)
  post('ship', ship)
  post('cancel-sj', cancelSj)
  post('revise', revise)
  post('unpack-scan', unpackScan)
  post('unpack-complete', unpackComplete)
  post('pick-gate', pickGate)
  post('pick-unit', pickUnit)
  post('pick-not-found', pickNotFound)
  post('stage-rack', stageRack)
  post('rts-lift', rtsLift)
  post('rts-gate', rtsGate)
  post('rts-place', rtsPlace)
  void userName
}
